import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <AuthShell title="Check your inbox" subtitle="If that account exists, a reset link is on its way.">
        <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-5 mb-6" data-testid="forgot-sent">
          <MailCheck className="w-8 h-8 text-[#D4AF37] mb-3" />
          <p className="text-[#D4D4D8] leading-relaxed">
            We've sent a password reset link to <span className="text-white font-medium">{email}</span>. The link expires in 60 minutes.
          </p>
        </div>
        <Link to="/login" className="text-[#D4AF37] hover:underline text-sm" data-testid="forgot-goto-login">Back to sign in</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="Enter your email and we'll send you a reset link.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="forgot-email-input" placeholder="you@company.com"
            className="bg-[#121214] border-white/10 text-white h-11" />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
          className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90">
          {loading ? "Sending…" : "Send reset link"}
        </Button>
      </form>
      <p className="text-center text-sm text-[#A1A1AA] mt-6">
        Remembered it? <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="forgot-goto-login">Sign in</Link>
      </p>
    </AuthShell>
  );
}
